import PptxGenJS from "pptxgenjs";
import { getReportV2 } from "@/lib/serverComm";
import type { Deck } from "@/common/decks";
import { exportDivAsPngWithInputFix } from "./chartExport";

const generateSlideTitle = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

const captureSlideChart = async (
  slideElement: HTMLElement,
): Promise<{ base64: string; width: number; height: number } | null> => {
  try {
    // Find the chart container within the slide
    const chartContainer = slideElement.querySelector(
      '[id^="chart-"]',
    ) as HTMLDivElement;
    if (!chartContainer) {
      console.warn("Chart container not found in slide");
      return null;
    }

    // Get dimensions before capture
    const width = chartContainer.offsetWidth;
    const height = chartContainer.offsetHeight;

    // Create a ref-like object for the export function
    const containerRef = { current: chartContainer };

    // Capture as base64
    const base64Data = await exportDivAsPngWithInputFix(
      containerRef,
      undefined,
      "base64",
    );

    return {
      base64: base64Data as string,
      width,
      height,
    };
  } catch (error) {
    console.error("Error capturing slide chart:", error);
    return null;
  }
};

export const exportDeckAsPptx = async (deck: Deck): Promise<void> => {
  try {
    // Create new presentation
    const pres = new PptxGenJS();

    // Set presentation properties
    pres.author = "PeopleScape Console";
    pres.company = "PeopleScape";
    pres.title = deck.name;

    // Constants / layout configuration
    const TITLE_PLACEHOLDER = "slideTitle";
    const SLIDE_WIDTH = 10; // pptxgen default for 16x9 layout
    const SLIDE_HEIGHT = 5.625; // pptxgen default height for 16x9
    const MARGIN_X = 0.1;
    const TITLE_HEIGHT = 0.6;
    const INITIAL_CHART_Y = 0.8; // space below title
    const HEADLINE_TEXT_HEIGHT = 0.6; // approximate height allocation for AI headline
    const HEADLINE_GAP_PX = 50; // distance below chart in pixels per requirement
    const HEADLINE_GAP_IN = HEADLINE_GAP_PX / 96; // convert to inches (pptx)
    const CHART_SCALE = 0.7; // scale from original px size
    const TEXT_COLOR = "363636";

    // Define a slide master with a real Title placeholder so it becomes the PPT slide title
    pres.defineSlideMaster({
      title: "PARITA_MASTER",
      background: { color: "FFFFFF" },
      objects: [
        {
          placeholder: {
            options: {
              name: TITLE_PLACEHOLDER,
              type: "title",
              x: MARGIN_X,
              y: 0.1,
              w: SLIDE_WIDTH - MARGIN_X * 2,
              h: TITLE_HEIGHT,
              fontSize: 22,
              bold: true,
              color: TEXT_COLOR,
              align: "center",
            },
          },
        },
      ],
    });

    // Filter only complete slides
    const completeSlides = deck.slides.filter(
      (slide) => slide.status === "complete",
    );
    if (completeSlides.length === 0) {
      throw new Error("No complete slides found to export");
    }

    // Prefetch unique reports for ai_headline in parallel
    const reportIds = Array.from(
      new Set(
        completeSlides
          .map((s) => s.report_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const reportHeadlineMap = new Map<string, string | undefined>();
    if (reportIds.length) {
      const reportPromises = reportIds.map(async (id) => {
        try {
          const rpt = await getReportV2(id);
          reportHeadlineMap.set(
            id,
            rpt.config?.metadata?.ai_headline as string | undefined,
          );
        } catch (e) {
          console.warn("[DeckExport] Failed to fetch report", id, e);
          reportHeadlineMap.set(id, undefined);
        }
      });
      await Promise.all(reportPromises);
    }

    // Build DOM lookup once (guard for SSR)
    const slideDomMap = new Map<string, HTMLElement>();
    if (typeof document !== "undefined") {
      document
        .querySelectorAll<HTMLElement>("[data-slide-id]")
        .forEach((el) => {
          const sid = el.getAttribute("data-slide-id");
          if (sid) slideDomMap.set(sid, el);
        });
    }

    const warnings: string[] = [];
    const pxToIn = (px: number) => px / 96;
    const scaleChartDims = (wPx: number, hPx: number) => {
      let wIn = pxToIn(wPx) * CHART_SCALE;
      let hIn = pxToIn(hPx) * CHART_SCALE;
      const maxW = SLIDE_WIDTH - MARGIN_X * 2;
      // Reserve space at bottom for gap + headline text + a small bottom margin
      const bottomReserve = HEADLINE_GAP_IN + HEADLINE_TEXT_HEIGHT + 0.2;
      const maxH = SLIDE_HEIGHT - INITIAL_CHART_Y - bottomReserve;
      if (wIn > maxW) {
        const ratio = maxW / wIn;
        wIn = maxW;
        hIn = hIn * ratio;
      }
      if (hIn > maxH) {
        const ratio = maxH / hIn;
        hIn = maxH;
        wIn = wIn * ratio;
      }
      return { wIn, hIn };
    };

    // Process slides
    for (let i = 0; i < completeSlides.length; i++) {
      const slideData = completeSlides[i];
      console.log(
        `[DeckExport] Processing ${i + 1}/${completeSlides.length}: ${slideData.title}`,
      );
      try {
        const aiHeadline = slideData.report_id
          ? reportHeadlineMap.get(slideData.report_id)
          : undefined;

        // Add new slide using master
        const slide = pres.addSlide({ masterName: "PARITA_MASTER" });

        // Populate title placeholder (real PPT title)
        slide.addText(slideData.title, { placeholder: TITLE_PLACEHOLDER });

        // Attempt chart capture
        let chartBase64: string | null = null;
        let chartWidthPx = 0;
        let chartHeightPx = 0;
        const domEl = slideDomMap.get(slideData.id);
        if (domEl) {
          const chartData = await captureSlideChart(domEl);
          if (chartData) {
            chartBase64 = chartData.base64;
            chartWidthPx = chartData.width;
            chartHeightPx = chartData.height;
          } else {
            warnings.push(
              `Slide "${slideData.title}": chart capture returned null.`,
            );
          }
        } else {
          warnings.push(
            `Slide "${slideData.title}": DOM element not found for capture.`,
          );
        }

        // Track bottom of content if future dynamic positioning needed (not required now with placeholder headline)
        if (chartBase64) {
          const { wIn, hIn } = scaleChartDims(chartWidthPx, chartHeightPx);
          // Center horizontally (respect minimal margin)
          const centeredX = Math.max(MARGIN_X, (SLIDE_WIDTH - wIn) / 2);
          slide.addImage({
            data: chartBase64,
            x: centeredX,
            y: INITIAL_CHART_Y,
            w: wIn,
            h: hIn,
            sizing: { type: "contain", w: wIn, h: hIn },
            altText: `Chart for ${slideData.title}`,
          });
          // content bottom tracked implicitly
        } else {
          slide.addText("[Chart could not be captured]", {
            x: MARGIN_X,
            y: INITIAL_CHART_Y,
            w: SLIDE_WIDTH - MARGIN_X * 2,
            h: 0.5,
            fontSize: 12,
            color: "666666",
            align: "center",
            italic: true,
          });
          // content bottom tracked implicitly
        }

        // Add AI headline dynamically positioned 50px below chart bottom (or placeholder area if no chart)
        if (aiHeadline) {
          // Determine headline Y: if chart image, use its bottom + gap; else base off chart area start + gap
          let headlineY = INITIAL_CHART_Y + HEADLINE_GAP_IN; // fallback when no chart
          if (chartBase64) {
            const { hIn } = scaleChartDims(chartWidthPx, chartHeightPx); // recompute to get final height
            headlineY = INITIAL_CHART_Y + hIn + HEADLINE_GAP_IN;
          }
          // Clamp so it doesn't overflow slide
          if (headlineY + HEADLINE_TEXT_HEIGHT > SLIDE_HEIGHT - 0.2) {
            headlineY = SLIDE_HEIGHT - 0.2 - HEADLINE_TEXT_HEIGHT;
          }
          slide.addText(aiHeadline, {
            x: MARGIN_X,
            y: headlineY,
            w: SLIDE_WIDTH - MARGIN_X * 2,
            h: HEADLINE_TEXT_HEIGHT,
            fontSize: 14,
            bold: true,
            color: TEXT_COLOR,
            align: "center",
          });
        }

        if (slideData.speaker_notes && slideData.speaker_notes.trim()) {
          slide.addNotes(slideData.speaker_notes);
        }
      } catch (slideErr) {
        console.error(
          `[DeckExport] Error rendering slide "${slideData.title}":`,
          slideErr,
        );
        warnings.push(`Slide "${slideData.title}" failed: ${String(slideErr)}`);
      }
    }

    // Generate safe filename
    let filename = generateSlideTitle(deck.name);
    if (!filename) filename = "deck";

    await pres.writeFile({ fileName: `${filename}.pptx` });

    if (warnings.length) {
      console.warn("[DeckExport] Export completed with warnings", warnings);
    }
    console.log(`[DeckExport] Exported successfully as ${filename}.pptx`);
  } catch (error) {
    console.error("Error exporting deck to PPTX:", error);
    throw error;
  }
};
